import type { Measure } from "~/modules/measures/types";
import { MEASURE_UNIT_LABELS } from "~/modules/measures/consts";
import { CopySlugBadge } from "~/modules/catalog/components/CopySlugBadge";
import { CatalogStatChip } from "~/modules/catalog/components/CatalogStatChip";
import { AssetList } from "~/modules/assets/components/AssetList";
import { ASSET_NAME_AND_TICKER_COLUMN_NON_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    ApiQueryEndpoints,
    API_QUERY_BASE_URLS,
    SortDirections,
    FilterOperators,
} from "~/modules/filters/types";
import { SectionHeader } from "~/components/SectionHeader";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { CatalogMetricTimeseriesChart } from "../../components/CatalogMetricTimeseriesChart";
import { getAssetTableColumnByCatalogSlug } from "../../components/CatalogAssetsTable/getAssetTableColumnByCatalogSlug";
import { CatalogMetricSlugs } from "../../types";
import { getCatalogCoveragePercent } from "../../lib/getCatalogCoveragePercent";

// // // //

interface Props {
    measure: Measure;
    totalAssetCount?: number;
}

export function MeasureIndividualPage(props: Props) {
    const { measure, totalAssetCount } = props;

    const assetsQuery: ApiQuery = {
        ...DEFAULT_ASSET_LIST_QUERY,
        filter: {
            operator: FilterOperators.And,
            filters: [
                {
                    field: AssetColumns.tokenCount,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
                {
                    field: measure.slug,
                    operator: FilterOperators.IsNotNull,
                    value: null,
                },
            ],
        },
        sort: {
            field: measure.slug,
            direction: SortDirections.desc,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    };

    return (
        <>
            <div className="flex items-center gap-2 flex-wrap mb-5">
                {measure.category && (
                    <CatalogStatChip label="Category">
                        {measure.category}
                    </CatalogStatChip>
                )}
                <CatalogStatChip label="Coverage">
                    {measure.asset_count.toLocaleString()}
                    {totalAssetCount !== undefined && (
                        <>
                            {" "}
                            / {totalAssetCount.toLocaleString()}
                            <span className="ml-1.5 text-primary">
                                {getCatalogCoveragePercent({
                                    totalAssetCount,
                                    assetCount: measure.asset_count,
                                })}
                            </span>
                        </>
                    )}
                </CatalogStatChip>
                <CatalogStatChip label="Unit">
                    {MEASURE_UNIT_LABELS[measure.unit]}
                </CatalogStatChip>
                <CopySlugBadge slug={measure.slug} />
            </div>

            <CatalogMetricTimeseriesChart metric={measure} />

            <SectionHeader label="Assets">
                <div className="flex justify-end items-center gap-2">
                    <VisualizationApiToolsButton
                        query={assetsQuery}
                        apiDocsBaseUrl={
                            API_QUERY_BASE_URLS[ApiQueryEndpoints.AssetV4]
                        }
                    />
                    <DataExportProvider>
                        {({ isLoading, exportCsv }) => (
                            <DownloadButton
                                disabled={isLoading}
                                onClick={() => {
                                    exportCsv({
                                        exportTableName:
                                            ExportTableNames.assets_v4,
                                        query: assetsQuery,
                                    });
                                }}
                            />
                        )}
                    </DataExportProvider>
                </div>
            </SectionHeader>

            <AssetList
                fixedHeader={true}
                fixedHeaderClassName="top-0"
                bordered={false}
                enableExport={false}
                enableApiTools={false}
                enableBasicSearch={false}
                enableProSearch={false}
                includeAdditionalNetworksRow={false}
                initialState={{
                    results: [],
                    sort: {
                        field: AssetColumns.name,
                        direction: SortDirections.asc,
                    },
                    pagination: {
                        page: 1,
                        perPage: 25,
                        pageCount: 1,
                        resultCount: 10,
                    },
                }}
                assetsQuery={assetsQuery}
                filterMetadata={[]}
                title="Assets"
                analyticsTableName="measure-assets"
                columns={[
                    ASSET_NAME_AND_TICKER_COLUMN_NON_EXPANDABLE,
                    // ENHANCEMENT - integrate getAssetTableColumnByCatalogSlug when columns are available for all metric items
                    ...getAssetTableColumnByCatalogSlug(
                        measure.slug as CatalogMetricSlugs,
                    ),
                ]}
            />
        </>
    );
}
